import { createRouter, createWebHistory } from '@ionic/vue-router';
import { RouteMeta, RouteRecordRaw } from 'vue-router';
import FindInvoice from '@/find-invoice/FindInvoice.vue';
import PayInvoice from '@/pay-invoice/PayInvoice.vue';
import PayeeNotFound from '@/PayeeNotFound.vue';
import PaymentComplete from '@/pay-invoice/PaymentComplete.vue';
import AppHome from '@/AppHome.vue';
import CreateAccount from '@/auth/CreateAccount.vue';
import LinkedResidents from '@/residents/LinkedResidents.vue';
import PaymentHistory from '@/payment-history/PaymentHistory.vue';
import PaymentMethods from '@/payment-methods/PaymentMethods.vue';
import RecurringPayments from '@/recurring-payments/RecurringPayments.vue';
import ForgotPassword from '@/auth/ForgotPassword.vue';
import { useAuth } from '@/auth/auth.composable';
import AdminPage from '@/admin/AdminPage.vue';
import AdminHome from '@/admin/AdminHome.vue';
import MultipleInvoicesFound from '@/find-invoice/MultipleInvoicesFound.vue';
import PayeeAdminHome from '@/payee-admin/PayeeAdminHome.vue';
import BatchProcessingErrors from'@/payee-admin/batch-upload/BatchProcessingErrors.vue';

interface EpayRouteMeta extends RouteMeta {
  isPublic?: boolean;
  isAdmin?: boolean;
}

type EpayRouteRecordRaw = RouteRecordRaw & { meta?: EpayRouteMeta };

const routes: Array<EpayRouteRecordRaw> = [
  {
    path: '/admin',
    name: 'Admin',
    component: AdminPage,
    meta: { isPublic: true },
  },
  {
    path: '/admin-home',
    name: 'AdminHome',
    component: AdminHome,
    meta: { isAdmin: true },
  },
  {
    path: '/admin-forgot-password',
    name: 'AdminForgotPassword',
    component: ForgotPassword,
    meta: { isPublic: true, isAdmin: true },
  },
  {
    path: '/payee-admin-home',
    name: 'PayeeAdminHome',
    component: PayeeAdminHome,
    meta: { isAdmin: true },
  },
  {
    path: '/batch-processing-errors/:batchId',
    name: 'BatchProcessingErrors',
    component: BatchProcessingErrors,
    meta: { isAdmin: true },
  },
  {
    path: '/:payeeId',
    name: 'Home',
    component: AppHome,
    meta: { isPublic: true },
  },
  {
    path: '/:payeeId/create-account',
    name: 'CreateAccount',
    component: CreateAccount,
    meta: { isPublic: true },
  },
  {
    path: '/:payeeId/payment-history',
    name: 'PaymentHistory',
    component: PaymentHistory,
  },
  {
    path: '/:payeeId/payment-methods',
    name: 'PaymentMethods',
    component: PaymentMethods,
  },
  {
    path: '/:payeeId/recurring-payments',
    name: 'RecurringPayments',
    component: RecurringPayments,
  },
  {
    path: '/:payeeId/:invoiceId',
    redirect: to => {
      return { name: 'PayInvoice', params: { payeeId: to.params.payeeId, invoiceId: to.params.invoiceId } };
    },
    meta: { isPublic: true },
  },
  {
    path: '/:payeeId/find-invoice',
    name: 'FindInvoice',
    component: FindInvoice,
    meta: { isPublic: true },
  },
  {
    path: '/:payeeId/choose-invoice',
    name: 'MultipleInvoices',
    component: MultipleInvoicesFound,
    meta: { isPublic: true },
  },
  {
    path: '/:payeeId/pay-invoice/:invoiceId',
    name: 'PayInvoice',
    component: PayInvoice,
    meta: { isPublic: true },
  },
  {
    path: '/:payeeId/invoice-paid',
    name: 'PaymentComplete',
    component: PaymentComplete,
    meta: { isPublic: true },
  },
  {
    path: '/',
    component: PayeeNotFound,
    meta: { isPublic: true },
  },
  {
    path: '/not-found',
    name: 'PayeeNotFound',
    component: PayeeNotFound,
    meta: { isPublic: true },
  },
  {
    path: '/:payeeId/linked-accounts',
    name: 'Residents',
    component: LinkedResidents,
  },
  {
    path: '/forgot-password',
    name: 'ForgotPassword',
    component: ForgotPassword,
    meta: { isPublic: true },
  }
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
});

router.beforeEach(async to => {
  const { isAuthenticated, isAuthenticationLoaded, authLoadingPromise } = useAuth();

  if (!isAuthenticationLoaded.value) {
    await authLoadingPromise;
  }

  if (to.fullPath !== '/admin-home' && !isAuthenticated.value && !to.meta.isPublic) {
    return { path: `/${to.params.payeeId}` };
  }
})

export default router;
